import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Skeleton } from 'antd';
import {
  ApartmentOutlined,
  BankOutlined,
  BookOutlined,
  CarOutlined,
  CheckOutlined,
  CrownOutlined,
  EditOutlined,
  FileTextOutlined,
  FolderOutlined,
  LockOutlined,
  ReadOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  SoundOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';

import { rolesAdminApi, type KbAccessLevel, type RoleDto } from '../lib/wikiApi';
import { PageTitle } from '../components/ds';
import { AccessMatrix } from './admin/AccessMatrix';
import './analytics/analytics.css';

/**
 * Роли и права (макет «RBAC» дизайн-кита FISHKA HUB) — поверх РЕАЛЬНОЙ
 * системы прав CRM. READ-ONLY: роли и их permissions принадлежат CRM
 * (модель Role + permissions.catalog.ts, редактор на crm.brasol.ru).
 *
 * Слева — список ролей (число пользователей). Справа — детали выбранной
 * роли: матрица «Доступ к разделам БЗ» уровнями (из kbAreas), «Возможности»
 * (из permissions) и «Пишет в пространства» (из WikiBlock.writeRoles).
 * Вместо мок-кнопки «Сохранить права» — честный бейдж «Права управляются
 * в CRM».
 */

// ─── Уровни доступа (легенда как в ките) ────────────────────────────
const LEVELS: Array<{ k: KbAccessLevel; label: string; c: string; bg: string }> = [
  { k: 0, label: 'Нет', c: 'var(--fk-text-muted)', bg: 'var(--fk-bg-subtle)' },
  { k: 1, label: 'Просмотр', c: '#0F6FC0', bg: 'rgba(0,150,255,.12)' },
  { k: 2, label: 'Редактирование', c: '#D9580E', bg: 'rgba(255,105,0,.12)' },
  { k: 3, label: 'Публикация', c: '#5C8A24', bg: 'rgba(124,179,66,.16)' },
];

// Иконки разделов БЗ (area.key из бэка).
const AREA_ICON: Record<string, JSX.Element> = {
  news: <SoundOutlined />,
  kb: <BookOutlined />,
  edu: <ReadOutlined />,
  std: <SafetyCertificateOutlined />,
  disk: <FolderOutlined />,
  ai: <ThunderboltOutlined />,
};

// ─── Иконка/цвет роли по её code (визуальная карта; не влияет на права) ─
function roleVisual(code: string): { icon: JSX.Element; color: string } {
  const c = code.toUpperCase();
  if (c === 'ADMIN' || c === 'SUPER' || c === 'SUPERADMIN')
    return { icon: <CrownOutlined />, color: 'var(--fk-error)' };
  if (c === 'PARTNER') return { icon: <ShopOutlined />, color: 'var(--fk-orange)' };
  if (c.includes('MANAGER') || c.includes('UPRAV'))
    return { icon: <ApartmentOutlined />, color: 'var(--fk-info)' };
  if (c.includes('MARKET')) return { icon: <SoundOutlined />, color: 'var(--fk-info)' };
  if (c.includes('COURIER') || c.includes('KURIER'))
    return { icon: <CarOutlined />, color: 'var(--fk-success)' };
  if (c.includes('CHEF') || c.includes('POVAR') || c.includes('COOK'))
    return { icon: <ShopOutlined />, color: '#9C5BD4' };
  if (c.includes('UK') || c.includes('COMPANY'))
    return { icon: <BankOutlined />, color: 'var(--fk-orange)' };
  if (c === 'OPERATOR') return { icon: <TeamOutlined />, color: 'var(--fk-indigo)' };
  return { icon: <UserOutlined />, color: 'var(--fk-indigo)' };
}

// ─── «Возможности» по части БЗ — выводим из реальных permissions ────
// READ-ONLY список: показываем наличие конкретного kb:/news:-права.
const CAP_DEFS: Array<{ codes: string[]; label: string; desc: string; icon: JSX.Element }> = [
  { codes: ['kb:admin'], label: 'Администрирование БЗ', desc: 'Блоки, категории, теги, роли', icon: <SafetyCertificateOutlined /> },
  { codes: ['kb:create', 'kb:edit_any'], label: 'Создание контента', desc: 'Статьи и черновики базы знаний', icon: <FileTextOutlined /> },
  { codes: ['kb:publish'], label: 'Публикация статей', desc: 'Публикация и снятие с публикации', icon: <CheckOutlined /> },
  { codes: ['kb:edit_any'], label: 'Правка чужих статей', desc: 'Редактирование любых материалов', icon: <EditOutlined /> },
  { codes: ['news:create', 'news:publish'], label: 'Новости', desc: 'Создание и публикация ленты УК', icon: <SoundOutlined /> },
  { codes: ['kb:opening_dashboard'], label: 'Дашборд открытия', desc: 'Прогресс открытия партнёров (УК)', icon: <RocketOutlined /> },
  { codes: ['kb:assign_employee_pwd'], label: 'Пароли сотрудников', desc: 'Выдача доступов сотрудникам iiko', icon: <LockOutlined /> },
  { codes: ['kb:comment'], label: 'Комментарии', desc: 'Обсуждения под статьями', icon: <ReadOutlined /> },
];
const SUPER = '*';

function roleHasCap(role: RoleDto, codes: string[]): boolean {
  if (role.permissions.includes(SUPER)) return true;
  return codes.some((code) => role.permissions.includes(code));
}

export function RolesPage({ embedded = false }: { embedded?: boolean } = {}): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['wiki-admin', 'roles'],
    queryFn: () => rolesAdminApi.list(),
    retry: false,
  });
  const [sel, setSel] = useState<string | null>(null);

  const roles = useMemo(() => data ?? [], [data]);
  const role = useMemo<RoleDto | null>(
    () => roles.find((r) => r.code === sel) ?? roles[0] ?? null,
    [roles, sel],
  );

  if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error)
    return (
      <Alert type="error" showIcon message="Не удалось загрузить роли" description={(error as Error).message} />
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {!embedded && (
        <PageTitle
          title="Роли и права"
          sub="Доступ партнёров и сотрудников к разделам Fishka Hub. Роли и права самих ролей настраиваются в CRM."
          style={{ marginBottom: 0 }}
        />
      )}

      {/* HUB 2.0: матрица «раздел × роль» — видимость и запись разделов базы (редактируемо). */}
      <AccessMatrix />

      <div className="fk-roles-grid" style={{ display: 'grid', gridTemplateColumns: '276px minmax(0,1fr)', gap: 20, alignItems: 'start' }}>
        {/* Список ролей */}
        <div className="fk-card" style={{ padding: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fk-text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', padding: '6px 10px 8px' }}>
            Роли · {roles.length}
          </div>
          {roles.map((r) => {
            const on = role?.code === r.code;
            const v = roleVisual(r.code);
            return (
              <button
                key={r.code}
                type="button"
                onClick={() => setSel(r.code)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
                  padding: '10px 12px', borderRadius: 'var(--fk-radius-md)', cursor: 'pointer',
                  marginBottom: 2, border: 'none', fontFamily: 'inherit',
                  background: on ? 'var(--fk-orange-soft)' : 'transparent',
                  transition: 'background var(--fk-dur-fast) var(--fk-ease)',
                }}
                onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = 'var(--fk-bg-subtle)'; }}
                onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: 36, height: 36, borderRadius: 6, background: `color-mix(in srgb, ${v.color} 12%, transparent)`, color: v.color, display: 'grid', placeItems: 'center', flexShrink: 0, fontSize: 17 }}>{v.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: on ? 700 : 600, color: on ? 'var(--fk-orange)' : 'var(--fk-text)' }}>
                    {r.name}
                    {r.isProtected && <LockOutlined style={{ fontSize: 11, color: 'var(--fk-text-muted)' }} />}
                  </span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--fk-text-muted)' }}>{r.userCount} пользователей</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Детали роли */}
        {role && <RoleDetail role={role} />}
      </div>

      <style>{`
        @media (max-width: 860px) {
          .fk-roles-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function RoleDetail({ role }: { role: RoleDto }): JSX.Element {
  const v = roleVisual(role.code);
  const caps = CAP_DEFS.map((c) => ({ ...c, on: roleHasCap(role, c.codes) }));

  return (
    <div>
      {/* Шапка роли + честный read-only бейдж вместо «Сохранить права» */}
      <div className="fk-card fk-card-pad" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ width: 52, height: 52, borderRadius: 8, background: `color-mix(in srgb, ${v.color} 12%, transparent)`, color: v.color, display: 'grid', placeItems: 'center', flexShrink: 0, fontSize: 26 }}>{v.icon}</div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{role.name}</h2>
            {role.isProtected && (
              <span style={{ background: 'var(--fk-bg-subtle)', color: 'var(--fk-text-2)', padding: '2px 8px', borderRadius: 'var(--fk-radius-pill)', fontSize: 11, fontWeight: 700 }}>Защищённая</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: 'var(--fk-text-2)', marginTop: 3 }}>
            {role.userCount} пользователей · код <code style={{ fontFamily: 'var(--fk-font-mono)', fontSize: 12 }}>{role.code}</code>
          </div>
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 'var(--fk-radius-pill)', background: 'var(--fk-bg-subtle)', border: '1px solid var(--fk-border)', color: 'var(--fk-text-2)', fontSize: 12.5, fontWeight: 700 }}>
          <LockOutlined /> Права роли управляются в CRM
        </span>
      </div>

      {/* Легенда уровней */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14, padding: '10px 14px', background: 'var(--fk-bg-subtle)', borderRadius: 'var(--fk-radius-md)', border: '1px solid var(--fk-border)' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fk-text-muted)' }}>Уровни доступа:</span>
        {LEVELS.map((lv) => (
          <span key={lv.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: lv.c }} /> {lv.label}
          </span>
        ))}
      </div>

      {/* Доступ к разделам — read-only бейджи уровня (выделен текущий) */}
      <div className="fk-card" style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--fk-border)', fontSize: 14, fontWeight: 700 }}>Доступ к разделам</div>
        {role.kbAreas.map((a, i) => (
          <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: i < role.kbAreas.length - 1 ? '1px solid var(--fk-border)' : 'none' }}>
            <span style={{ width: 34, height: 34, borderRadius: 6, background: 'var(--fk-bg-subtle)', color: 'var(--fk-text-2)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>{AREA_ICON[a.key] ?? <BookOutlined />}</span>
            <span style={{ flex: 1, fontSize: 14.5, fontWeight: 600 }}>{a.label}</span>
            {/* Read-only сегменты: текущий уровень подсвечен. */}
            <div style={{ display: 'flex', background: 'var(--fk-bg-subtle)', borderRadius: 'var(--fk-radius-pill)', padding: 3, border: '1px solid var(--fk-border)' }}>
              {LEVELS.map((lv) => {
                const on = a.level === lv.k;
                return (
                  <span
                    key={lv.k}
                    style={{ padding: '6px 13px', borderRadius: 'var(--fk-radius-pill)', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', background: on ? lv.bg : 'transparent', color: on ? lv.c : 'var(--fk-text-muted)' }}
                  >
                    {lv.label}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Возможности — read-only из permissions */}
      <div className="fk-card" style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--fk-border)', fontSize: 14, fontWeight: 700 }}>Возможности по базе знаний</div>
        <div className="fk-caps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)' }}>
          {caps.map((c, i) => (
            <div
              key={c.label}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
                borderBottom: i < caps.length - (caps.length % 2 === 0 ? 2 : 1) ? '1px solid var(--fk-border)' : 'none',
                borderRight: i % 2 === 0 ? '1px solid var(--fk-border)' : 'none',
                opacity: c.on ? 1 : 0.55,
              }}
            >
              <span style={{ width: 34, height: 34, borderRadius: 6, background: c.on ? 'var(--fk-orange-soft)' : 'var(--fk-bg-subtle)', color: c.on ? 'var(--fk-orange)' : 'var(--fk-text-muted)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>{c.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{c.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--fk-text-muted)' }}>{c.desc}</div>
              </div>
              {/* Статичный индикатор (read-only): есть/нет права. */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: c.on ? 'var(--fk-success)' : 'var(--fk-text-muted)' }}>
                {c.on ? <CheckOutlined /> : <LockOutlined />}
                {c.on ? 'Есть' : 'Нет'}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Пишет в пространства — из WikiBlock.writeRoles */}
      <div className="fk-card fk-card-pad">
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Пишет в пространства</div>
        <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginBottom: 12 }}>
          Разделы базы знаний, доступные роли на запись. Настраивается в матрице выше или в
          «Администрирование → Разделы».
        </div>
        {role.writeBlocks.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--fk-text-muted)' }}>Нет пространств с правом записи для этой роли.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {role.writeBlocks.map((b) => (
              <span
                key={b.code}
                title={b.openToAll ? 'Открыто всем с правом kb:create' : 'Явно выдано этой роли'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  borderRadius: 'var(--fk-radius-pill)', fontSize: 13, fontWeight: 600,
                  border: '1px solid var(--fk-border)',
                  background: b.openToAll ? 'var(--fk-bg-subtle)' : 'var(--fk-orange-soft)',
                  color: b.openToAll ? 'var(--fk-text-2)' : 'var(--fk-orange)',
                }}
              >
                <FolderOutlined /> {b.name}
                {b.openToAll && <span style={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>· открыто всем</span>}
              </span>
            ))}
          </div>
        )}
      </div>

      {role.isProtected && (
        <div style={{ fontSize: 12.5, color: 'var(--fk-text-muted)', marginTop: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
          <LockOutlined /> Защищённая роль — обладает расширенным доступом и не редактируется.
        </div>
      )}

      <style>{`
        @media (max-width: 620px) {
          .fk-caps-grid { grid-template-columns: 1fr !important; }
          .fk-caps-grid > div { border-right: none !important; }
        }
      `}</style>
    </div>
  );
}
